import { Card } from "./Card";
import { FoundationPile } from "./FoundationPile";
import { OuterPile } from "./OuterPile";
import { GameConstants } from "../../GameConstants";
import { GameModel } from "../../GameModel";
import { GameVars } from "../../GameVars";

export class Board extends Phaser.Group {

    public static currentInstance: Board;
    public static cards: Card[];
    
    private static FOUNDATION_PILE_PX = -360;
    private static FOUNDATION_PILE_PY = 480;
    private static FOUNDATION_PILE_DX = 84;
    
    public dealData: DealData[];
    public boundingRectangleFoundationPiles: Phaser.Rectangle;
    public outerBoundingRectangleFoundationPiles: Phaser.Rectangle;
    public foundationPiles: FoundationPile[];
    public outerPiles: OuterPile[];

    public highlightSprite: Phaser.Sprite;

    constructor(game: Phaser.Game) {

        super(game, null, "board");
        
        Board.currentInstance = this;
    
        this.highlightSprite = null;

        this.dealData = this.game.cache.getJSON("card-positions");
        
        this.scale.x = GameVars.scaleX;
        
        this.x = GameConstants.GAME_WIDTH / 2;
        this.y = 30;
        
        Board.cards = new Array();
        this.foundationPiles = new Array();
        this.outerPiles = new Array();
        
        this.addFoundationPilesCollisionRectangle();
        this.addOuterPiles();
        
        if (GameVars.getInstance().retrieveSavedGame) {
            this.retrieveSavedGame();
        } else if (GameVars.getInstance().simulatesStartedGame) {
            this.addCardsToAnStartedGame();  
        } else {
            this.addFoundationPiles();
            this.addCardsToOuterPiles();
        }
    }
    
    public dealCards(): void {
        
        let card: Card;
        
        for (let i = 0; i < Board.cards.length; i++) {
            
            card = Board.cards[i];

            card.dealToPile(this.dealData[i]);
        }
    }
            
    public checkCollisionsWithFoundationPiles( card: Card): number {
        
        let pile: FoundationPile;
        let d: number;
        
        let collisions: PileCollision[] = [];
        
        for (let i = 0; i < 8; i++) {
            
            pile = this.foundationPiles[i];
            
            if ( pile.boundingRectangle.intersects(card.boundingRectangle, 1)) {
                
                d = Math.sqrt((pile.pileX - card.ccx) * (pile.pileX - card.ccx) + (pile.pileY - card.ccy) * (pile.pileY - card.ccy));
                collisions.push({d: d, i: pile.i}); 
            }
        }
        
        if ( collisions.length === 2) {
            
            if (collisions[0].d > collisions[1].d) {
                return collisions[1].i;
            } else {
                return collisions[0].i;
            }
            
        } else if (collisions.length === 1) {
            return collisions[0].i;
        } else {
            return -1;
        }
    }
    
    public checkCollisionsWithOuterPiles( card: Card): number {

        let pile: OuterPile;
        let d: number;
        
        let collisions: PileCollision[] = new Array();
        
        for (let i  = 0; i < 16; i++) {
            
            pile = this.outerPiles[i];
            
            d = Math.sqrt ((pile.pileX - card.ccx) * (pile.pileX -  card.ccx) + (pile.pileY -  card.ccy) * (pile.pileY - card.ccy));
            
            if ( d < Card.COLLISION_CIRCLE_RADIUS + OuterPile.COLLISION_CIRCLE_RADIUS && pile.i !== card.outerPileIndex) {
                collisions.push({d: d, i: pile.i});
            }
        }
        
        if ( collisions.length > 1) {
            
            if (collisions[0].d > collisions[1].d) {
                return collisions[1].i;
            } else {
                return collisions[0].i;
            }
            
        } else if (collisions.length === 1) {
            return collisions[0].i;
        } else {
            return -1;
        }
    }
    
    public addCardToFoundationPile(card: Card, pileIndex: number): void {

        let foundationPile: FoundationPile = this.foundationPiles[pileIndex - 1];            
        foundationPile.addCard(card);
    }
    
    public addCardToOuterPile(card: Card, pileIndex: number): void {
        
        let outerPile: OuterPile;
        
        if (pileIndex > 0) {
            
            outerPile = this.outerPiles[pileIndex - 1];
            outerPile.addCard(card);
            
        } else {
            
            // returns card to its original outer pile
            outerPile = this.outerPiles[card.outerPileIndex - 1];
            outerPile.returnCardToPile(card);
        }
    }
    
    public onCardStartedDragging( card: Card): void {
    
        this.add(card);
        
        if (this.highlightSprite) {
            this.highlightSprite.destroy();
            this.highlightSprite = null;
        }
    }
    
    public pause(): void {

        Board.cards.forEach((card: Card) =>
            card.disableDrag()
        );
    }
    
    public resume(): void {

        Board.cards.forEach((card: Card) =>
            card.enableDrag()
        );
    }
    
    public reshuffle(): void {
        
        if (this.highlightSprite) {
            this.highlightSprite.destroy();
            this.highlightSprite = null;
        }
        
        let outerPile: OuterPile;
        
        let d = 0;
        let delay = 0;
        
        for (let i = 0; i < this.outerPiles.length ; i++) {
            outerPile = this.outerPiles[i];
            this.bringToTop(outerPile);
            
            d = outerPile.shuffle();
            
            if ( d > delay) {
                delay = d;
            }
        }
        
        Card.cardTweening = true;
        
        this.game.time.events.add(delay, function(): void {
                Card.cardTweening = false;
        }, this);
    }
    
    public showHint(outerPileIndex: number): void {
        
        this.highlightSprite = this.outerPiles[outerPileIndex].showHint();
    }
    
    public undo(): void {
        
        let card: Card;
        
        if (GameVars.getInstance().destinyPileData.isFoundation) {
            let destinyFoundationPile: FoundationPile = this.foundationPiles[GameVars.getInstance().destinyPileData.i - 1];
            card = destinyFoundationPile.getUpperCard();
        } else {
            let destinyOuterPile: OuterPile = this.outerPiles[GameVars.getInstance().destinyPileData.i - 1];
            card = destinyOuterPile.getUpperCard();
        }
        
        if (GameVars.getInstance().originPileData.isFoundation) {
            
            let originFoundationPile: FoundationPile = this.foundationPiles[GameVars.getInstance().originPileData.i - 1];
            this.bringToTop(originFoundationPile);
            originFoundationPile.addCard(card, false, true);
        
        } else {
            
            let originOuterPile: OuterPile = this.outerPiles[GameVars.getInstance().originPileData.i - 1];
            originOuterPile.addCard(card, false, true);
        }
    }
    
    private addFoundationPiles(): void {

        let card: Card; 
        let foundationPile: FoundationPile;
        
        for (let i = 0; i < 4; i++) {

            // the aces
            foundationPile = new FoundationPile(this.game,  Board.FOUNDATION_PILE_PX   + i * Board.FOUNDATION_PILE_DX, Board.FOUNDATION_PILE_PY );
            foundationPile.kingsPile = false;
            foundationPile.i = i + 1;
            this.addAt(foundationPile, i);
            this.foundationPiles.push(foundationPile);
            
            card = new Card(this.game, {value: "A", suit: i});
            foundationPile.addCard(card, true);
            Board.cards.push(card);
        }     
            
        for (let i = 0; i < 4; i++) { 
            // the kings
            foundationPile = new FoundationPile(this.game,  Board.FOUNDATION_PILE_PX + 464 + i * Board.FOUNDATION_PILE_DX, Board.FOUNDATION_PILE_PY);
            foundationPile.kingsPile = true;
            foundationPile.i =  i + 5;
            this.addAt(foundationPile, i + 4);
            this.foundationPiles.push(foundationPile);
            
            card = new Card(this.game, {value: "K", suit: i});
            foundationPile.addCard(card, true);
            Board.cards.push(card);
        }
    }
    
    private addOuterPiles(): void {

        let x: number;
        let y: number;
        let a: number;
        let pile: OuterPile;
        
        for (let i = 0; i < 16; i++) {

            a = Math.PI / 180 * ( -118 + 56 / 15 * i);
            
            x = 900 * Math.cos(a);
            y = 600 * Math.sin(a) + GameConstants.CENTER_OUTER_CARDS_Y;

            pile = new OuterPile(this.game, x, y, a / Math.PI * 180 + 90, i + 1);
            this.add(pile);
            this.outerPiles.push(pile);
        }
    }
    
    private addCardsToOuterPiles(): void {
        
        let card: Card;
        let pile: OuterPile;
        
        for (let j = 0; j < 6; j++) {
            for ( let k = 0; k < 16; k++) {
                pile = this.outerPiles[k];
                
                card = new Card(this.game, GameModel.currentInstance.getCardValue());
                
                pile.addCard(card, true);
                Board.cards.push(card);
            }
        }
    }
    
    private addCardsToAnStartedGame(): void {
            
        let cards: any =  GameModel.currentInstance.getCardsArraysForAnStartedGame();
        let foundationPilesCards: any = cards[0];

        let foundationPile: FoundationPile;
        let card: Card;
        let j: number;
        
        for (let i = 0; i < 4; i++) {
            
            // the aces
            foundationPile = new FoundationPile(this.game,  Board.FOUNDATION_PILE_PX + i * Board.FOUNDATION_PILE_DX, Board.FOUNDATION_PILE_PY );
            foundationPile.kingsPile = false;
            foundationPile.i = i + 1;
            this.addAt(foundationPile, i);
            this.foundationPiles.push(foundationPile);
            
            for (j = 0; j < foundationPilesCards[i].length; j++) {
                card = new Card(this.game, foundationPilesCards[i][j]);                
                foundationPile.addCard(card);
                card.enableDrag();
                Board.cards.push(card);
            }
        }
            
        // the kings
        for (let i = 0; i < 4; i++) {    
        
            foundationPile = new FoundationPile(this.game,  Board.FOUNDATION_PILE_PX + 464 + i * Board.FOUNDATION_PILE_DX, Board.FOUNDATION_PILE_PY);
            foundationPile.kingsPile = true;
            foundationPile.i = i + 5;
            this.addAt(foundationPile, i + 4);
            this.foundationPiles.push(foundationPile);
        
            for (j = 0; j < foundationPilesCards[ i + 4].length; j++) {
                card = new Card(this.game, foundationPilesCards[i + 4][j]);
                foundationPile.addCard(card);
                card.enableDrag();
                Board.cards.push(card);
            }	
        }
            
        let outerCards: CardValue[] = cards[1];
        let pile: OuterPile;    
        
        let k = 0;
                    
        while (outerCards.length > 0) {
            
            if (Math.random() > .5) {
                card = new Card(this.game, outerCards.pop());
                card.enableDrag();
                pile = this.outerPiles[k];
                pile.addCard(card);
                Board.cards.push(card);	 
            }
            
            k = k < 15 ? k + 1 : 0;
        }
    }
    
    private retrieveSavedGame(): void {
        
        this.alpha = 0;
        this.game.add.tween(this)
            .to({alpha: 1 }, 750 , Phaser.Easing.Cubic.Out, true, 1000);
        
        let savedData: GameStateData = GameVars.getInstance().savedGameStateData;
        
        let i: number;
        let j: number;
        let cardValue: CardValue;
        let outerPilesData: CardValue[][] = savedData.outerPiles;
        let card: Card;
        let pile: OuterPile;
            
        for ( i = 0; i < outerPilesData.length; i++) {
            for ( j = 0; j < outerPilesData[i].length; j++) {
                
                cardValue = outerPilesData[i][j];
                
                if (cardValue) {
                    card = new Card(this.game, outerPilesData[i][j]);
                    card.enableDrag();
                    pile = this.outerPiles[i];
                    pile.addCard(card);
                    Board.cards.push(card);
                }  	
            }
        }
        
        let foundationPilesData: CardValue[][] = savedData.foundationPiles;
        
        let foundationPile: FoundationPile;
            
        for (let i: number = 0; i < 4; i++) {
                
            // the aces
            foundationPile = new FoundationPile(this.game,  Board.FOUNDATION_PILE_PX   + i * Board.FOUNDATION_PILE_DX, Board.FOUNDATION_PILE_PY );
            foundationPile.kingsPile = false;
            foundationPile.i = i + 1;
            this.add(foundationPile);
            this.foundationPiles.push(foundationPile);
            
            for (j = 0; j < foundationPilesData[i].length; j++) {
                
                cardValue = foundationPilesData[i][j];
                
                if (cardValue) {
                    card = new Card(this.game, cardValue);
                    foundationPile.addCard(card);
                    card.enableDrag();
                    Board.cards.push(card);
                } else {
                    foundationPile.coverPile();
                }
            }
        }
        
        // the kings
        for (let i = 0; i < 4; i++) {  
            
            foundationPile = new FoundationPile(this.game,  Board.FOUNDATION_PILE_PX + 464 + i * Board.FOUNDATION_PILE_DX, Board.FOUNDATION_PILE_PY);
            foundationPile.kingsPile = true;
            foundationPile.i = i + 5;
            this.add(foundationPile);
            this.foundationPiles.push(foundationPile);
        
            for (j = 0; j < foundationPilesData[i + 4].length; j++) {
                
                cardValue = foundationPilesData[i + 4][j];
                
                if (cardValue) {
                    card = new Card(this.game, cardValue);
                    foundationPile.addCard(card);
                    card.enableDrag();
                    Board.cards.push(card);
                } else {
                    foundationPile.coverPile();
                }
            }
            }
    }
    
    private addFoundationPilesCollisionRectangle(): void {
        
        // define bounding rectangle for all the piles
        let rectangle_px = -1000;
        let rectangle_py = Board.FOUNDATION_PILE_PY - GameConstants.HALF_CARD_HEIGHT;
        // let rectangleWidth: number = 3 * Board.FOUNDATION_PILE_DX + GameConstants.CARD_WIDTH;
        let rectangleWidth = 2000;
        let rectangleHeight = 500;
        
        this.boundingRectangleFoundationPiles = new Phaser.Rectangle(rectangle_px, rectangle_py, rectangleWidth, rectangleHeight );
        this.outerBoundingRectangleFoundationPiles = new Phaser.Rectangle(rectangle_px - 15, rectangle_py - 25, rectangleWidth + 30 , rectangleHeight + 50);
        
        if (GameConstants.SHOW_BOUNDING_AREAS) {
            const boundingRectangleSprite: Phaser.Sprite = new Phaser.Sprite(this.game, rectangle_px, rectangle_py, this.game.cache.getBitmapData("white-square"));
            boundingRectangleSprite.scale.setTo(rectangleWidth / 16, rectangleHeight / 16);
            boundingRectangleSprite.alpha = .3;
            this.add(boundingRectangleSprite);
        }
    }
}
